import type { Feature, FeatureCollection, Point } from "geojson";
import { type ComponentType, type RefObject, useEffect, useRef, useState } from "react";
import { unitShown } from "./maneuverUnits";

import type { Deployment, MissionSymbol, Phase, SubordinateUnit } from "../../api/types";
import { useUi } from "../../state/ui";
import { useTicker } from "../manoeuvre/motion";
import { lang } from "../../i18n/i18n";
import { type Clearance, clearance, translateExpression } from "./missionClearance";
import { MISSION_PREFIX, NEUTRAL_IMAGE, neutralChipLabel, neutralIcon } from "./missionImages";
import {
  frameMissionSymbols,
  keyMissionSymbols,
  type KeyedSymbol,
  pairMissionSymbols,
  shownMissionImage,
  type ShownSymbol,
  type SymbolFrame,
  type SymbolPlan,
} from "./missionSymbols";
import { MAP_EXIT_MS, smoothstep, usePhaseClock } from "./phaseTransition";
import { useShownDeployment } from "../manoeuvre/useOrientation";
import { dataSignature, type DataSignature, isDataTransition, signatureChanged } from "./UnitsLayers";
import { rootShift, useZoomLevels } from "./zoomLevels";

/** Symboles de mission du temps affiché, posés comme sur la carte de l'iPad (MissionSymbols.swift) : image du
 *  catalogue iPad à son échelle (0,7 ami, 0,8 ennemi, 1,4 NEUTRALISER / DÉTRUIRE), tournée dans le sens horaire
 *  du cap de sa cible + 90° (les flèches pointent vers l'ouest à 0°) et tournant avec la carte. JALONNER et
 *  FREINER : l'unité au centre de la boucle du symbole (demande d'Alexis du 14/10/2026 ; l'iPad les décalait de
 *  100 points à gauche et 30 vers le haut à l'écran). Le décalage vient du serveur (`offset`, en points de
 *  l'image) et passe par `icon-offset`, que Mapbox et MapLibre ajoutent à l'image avant de la multiplier par
 *  `icon-size` et de la tourner de `icon-rotate` : le centre de la boucle reste sur l'unité quels que soient le
 *  cap, l'échelle et le zoom (`iconPixelOnScreen`). Un décalage d'écran (`icon-translate`) ne tournerait pas, et
 *  une position géographique décalée ne tiendrait qu'à un zoom. Au-dessus des unités, comme sur l'iPad.
 *
 *  Texte à l'endroit (demande d'Alexis du 14/10/2026 : « Le texte 'JAL' est à l'envers ») : une image dont le
 *  texte, tourné avec elle et avec la carte, se lirait tête en bas est remplacée par son autre variante
 *  (`shownMissionImage` : `0To180` ↔ `180To0`, ou variante « …Retourne »). Les images tournent avec la carte
 *  (`icon-rotation-alignment` « map ») : le choix dépend donc de l'orientation de la carte. Une expression de
 *  style ne peut pas lire cette orientation (ni Mapbox ni MapLibre n'exposent le cap aux expressions) : le
 *  composant suit l'événement « rotate » de la carte, recalcule à chaque image le nom des images montées
 *  (quelques comparaisons par symbole, aucun rendu React) et ne se redessine que quand l'un d'eux change, au
 *  passage d'un seuil. Les données ne partent vers la source (setData) que si elles ont changé
 *  (`collection`) ; toutes les variantes sont chargées au démarrage (`loadMissionImages`) : la bascule se fait
 *  d'une image à l'autre sans image manquante ni clignotement.
 *
 *  Transitions (docs/14 §2.10) : symboles appariés par (unité, mission, rang dans la mission) (missionSymbols.ts) ;
 *  une paire gardée glisse, pivote et change d'échelle ; une image qui change à position égale se fond dans la
 *  nouvelle ; sinon l'ancien symbole s'efface sur place pendant que le nouveau glisse et apparaît. D'un temps à
 *  l'autre (2,5 s) ou à la réponse du serveur (900 ms), sur l'horloge commune ; hors transition (calque masqué ou
 *  affiché), en `MAP_EXIT_MS`. Opacité par entité (`icon-opacity: ["get", "opacity"]`), facteur d'ouverture du
 *  dossier compris.
 *
 *  Carte zoomée (registre A96) : à une profondeur inférieure, les symboles affichés sont ceux de ses unités
 *  (`levelMissionSymbols`), à la place de ceux du dossier ; au changement de niveau, ceux du niveau quitté
 *  s'effacent sur place et ceux du nouveau apparaissent en fondu (`pairMissionSymbols`).
 *
 *  Écart à faible zoom (demande d'Alexis du 04/11/2026 : « Lorsque les unités apparaissent, leur symbole de mission
 *  est superposé à l'unité ») : le serveur pose un symbole à une distance fixe sur le terrain, que le dézoom réduit
 *  à quelques pixels, alors que les images gardent leur taille à l'écran. Chaque symbole garde l'écart à son unité
 *  (`lead`, interpolé pendant les transitions) ; `clearance` (missionClearance.ts) en tire, au cap de la carte,
 *  l'axe unité → symbole et la distance à l'écran qui dégage l'unité. Un symbole qui peut chevaucher son unité est
 *  dessiné dans son propre calque, poussé le long de cet axe par `icon-translate` (repère de l'écran) : une
 *  interpolation exponentielle sur le zoom, évaluée par le moteur à chaque image, qui reproduit exactement ce qui
 *  manque à la distance du serveur et s'annule au zoom où elle suffit (fort zoom : même point, rendu inchangé).
 *  Rejetés : un `icon-offset` interpolé selon le zoom (Mapbox et MapLibre acceptent l'expression, mais évaluent ce
 *  réglage de mise en page au zoom entier des tuiles : le symbole sautait à chaque niveau entier) et une position
 *  recalculée à chaque image de zoom (la source GeoJSON passe par le worker : les mises à jour arrivaient par
 *  à-coups, le symbole s'éloignait puis revenait de plusieurs dizaines de pixels). `icon-translate` n'est pas
 *  piloté par les données : d'où un calque par symbole poussé, filtré sur sa clé, placé sous les autres symboles
 *  de mission. L'événement « rotate » recalcule les paliers au cap de la carte (axe et emprise vus à l'écran).
 *  Jamais poussés : symboles posés sur l'unité ou sur la cible (NEUTRALISER, DÉTRUIRE), boucle de JALONNER et
 *  FREINER centrée sur l'unité, queue des flèches d'itinéraire posée sur elle (`offset`). */

/** Délai au-delà duquel des symboles retenus (réponse reçue, transition de données attendue) suivent les
 *  données sans elle. */
const HOLD_MS = 300;

interface EngineParts {
  Source: ComponentType<any>;
  Layer: ComponentType<any>;
  /** Carte (orientation exacte et événement « rotate ») ; sans elle, carte au nord. */
  mapRef?: RefObject<{ getMap(): MapBearing } | null>;
}

/** Ce que le composant lit de la carte : son orientation et l'événement « rotate ». */
interface MapBearing {
  getBearing(): number;
  on(type: "rotate", listener: () => void): unknown;
  off(type: "rotate", listener: () => void): unknown;
}

/** Noms des images montées à l'écran, à cette orientation de la carte : signature des bascules ; un symbole
 *  écarté de son unité y ajoute sa portée (`clearance`) et le cap, dont dépend sa poussée. */
function shownImages(frames: readonly SymbolFrame[], bearing: number): string {
  return frames
    .map((s) => {
      const image = s.image === NEUTRAL_IMAGE ? "" : shownMissionImage(s.image, s.rotation, bearing, s.offset).image;
      const reach = clearanceOf(s, bearing)?.reach;
      // Poussée tracée dans le repère de l'écran : suit le cap.
      return reach === undefined ? image : `${image}@${reach.toFixed(1)}@${bearing.toFixed(1)}`;
    })
    .join("|");
}

/** Écart du symbole à son unité, au cap de la carte (null : jamais poussé). */
function clearanceOf(s: SymbolFrame, bearing: number): Clearance | null {
  const image = s.image === NEUTRAL_IMAGE ? s.image : shownMissionImage(s.image, s.rotation, bearing, s.offset).image;
  return clearance(s, bearing, image, neutralChipLabel(s.mission, lang()));
}

/** Symbole du serveur et point de son unité (`lead` des symboles affichés). */
type AnchoredSymbol = MissionSymbol & { anchor: [number, number] | null };

const LAYOUT = {
  "icon-image": ["get", "icon"],
  "icon-size": ["get", "scale"],
  "icon-rotate": ["get", "rotation"],
  "icon-rotation-alignment": "map",
  "icon-allow-overlap": true,
  "icon-ignore-placement": true,
};

/** Calque d'un symbole poussé (suivi de la clé du symbole). */
const OWN_LAYER_PREFIX = "mission-symbols-own:";
/** Symbole poussé : poussée dans le repère de l'écran (`icon-translate-anchor` « viewport »). Image toujours tournée
 *  avec la carte, mais posée face à l'écran (`icon-pitch-alignment` « viewport » ; carte à plat : rendu identique) :
 *  posée sur la carte, Mapbox tournait la poussée une seconde fois du cap (mesuré dans le navigateur ; MapLibre
 *  donne le même résultat que Mapbox avec ce réglage). */
const OWN_LAYOUT = { ...LAYOUT, "icon-pitch-alignment": "viewport" };

/** JALONNER, FREINER : image décalée (points de l'image), tournée et mise à l'échelle avec elle. */
const SHIFTED_LAYOUT = { ...LAYOUT, "icon-offset": ["get", "offset"] };

/** Origine d'un symbole nouveau pendant un changement de temps : le même décalage par rapport à son unité qu'à
 *  l'arrivée (il suit le déplacement de l'unité, comme avant) — sauf si l'unité avait déjà un symbole affiché, dont
 *  il part alors (`pairMissionSymbols` : symbole de la même unité au même rang). */
export function phaseEntryOrigin(
  data: Deployment | undefined,
  from: Phase | null,
  phase: Phase,
  shown: readonly ShownSymbol[],
): ((to: KeyedSymbol) => [number, number] | undefined) | undefined {
  if (!from || !data) return undefined;
  const units = new Map(data.units.map((u) => [u.key, u]));
  const withSymbols = new Set(shown.filter((s) => s.opacity > 0).map((s) => s.unit));
  return (to) => {
    if (withSymbols.has(to.unit)) return undefined;
    const unit = units.get(to.unit);
    const before = unit?.positions[from];
    const after = unit?.positions[phase];
    if (!before || !after) return undefined;
    return [to.at[0] + before.lon - after.lon, to.at[1] + before.lat - after.lat];
  };
}

/** Symboles de mission de la profondeur affichée de la carte zoomée (registre A96, demande d'Alexis du 20/10/2026 :
 *  « lorsque l'utilisateur zoome et que les N-1, N-2… AMI et ENI apparaissent, leurs symboles de mission doivent
 *  aussi être affichés ») : ceux que le serveur pose pour chaque unité de la profondeur (mêmes règles, images,
 *  échelles et orientation que la carte du dossier), décalés comme leurs unités du déplacement de l'unité du
 *  dossier dont elles descendent depuis le calcul (`rootShift`) ; ceux des appuis et soutiens cachés, cachés avec
 *  eux. Les symboles du niveau quitté s'effacent, ceux du nouveau apparaissent (`pairMissionSymbols`), comme les
 *  unités qui éclatent ou se regroupent ; un seul niveau à la fois (registre J13). */
export function levelMissionSymbols(
  level: readonly SubordinateUnit[],
  symbols: Partial<Record<Phase, MissionSymbol[]>>,
  phase: Phase,
  deployed: Deployment["units"],
  roots: Record<string, Partial<Record<Phase, [number, number]>>>,
  supportsVisible: boolean,
): AnchoredSymbol[] {
  const units = new Map(level.map((u) => [u.key, u]));
  const current = new Map(deployed.map((u) => [u.key, u]));
  return (symbols[phase] ?? []).flatMap((s) => {
    const unit = units.get(s.unit);
    if (!unit || !unitShown(unit, supportsVisible)) return [];
    const root = unit.source === "dossier" ? unit.key : unit.root;
    const at = current.get(root)?.positions[phase];
    const [dx, dy] = rootShift(roots, root, phase, at ? [at.lon, at.lat] : undefined);
    const own = unit.positions[phase];
    return [{
      ...s,
      coordinates: [s.coordinates[0] + dx, s.coordinates[1] + dy] as [number, number],
      anchor: own ? ([own.lon + dx, own.lat + dy] as [number, number]) : null,
    }];
  });
}

interface Plan {
  plan: SymbolPlan;
  /** Transition de l'horloge commune qui porte le plan ; null : plan local (`MAP_EXIT_MS`). */
  clock: number | null;
  /** Numéro de transition de l'horloge au moment du plan (une nouvelle transition en ouvre un autre). */
  seen: number;
  started: number;
  /** Entrées qui ont produit le plan (données, temps, calques visibles). */
  inputs: unknown[];
}

/** Symboles de la carte du dossier, avec le point de leur unité au temps affiché. */
export function anchoredSymbols(symbols: readonly MissionSymbol[], units: Deployment["units"], phase: Phase): AnchoredSymbol[] {
  const byKey = new Map(units.map((u) => [u.key, u]));
  return symbols.map((s) => {
    const p = byKey.get(s.unit)?.positions[phase];
    return { ...s, anchor: p ? [p.lon, p.lat] : null };
  });
}

export function MissionSymbolsLayers({ Source, Layer, mapRef }: EngineParts) {
  const { data, isPlaceholderData } = useShownDeployment(); // aperçu du sens de la manœuvre (docs/14 §2.14)
  const phase = useUi((s) => s.phase);
  const visible = useUi((s) => s.unitsVisible);
  const supportsVisible = useUi((s) => s.supportUnitsVisible);
  const exerciseId = useUi((s) => s.deploymentParams?.exercise_id ?? null);
  const level = useZoomLevels((s) => s.units);
  const levelSymbols = useZoomLevels((s) => s.symbols);
  const roots = useZoomLevels((s) => s.roots);
  const { id, from, progress, kind } = usePhaseClock();
  const reveal = usePhaseClock((s) => s.reveal);
  const displayed = useRef<SymbolFrame[]>([]);
  const current = useRef<Plan | null>(null);
  const signature = useRef<DataSignature | null>(null);
  // Réponse du serveur reçue, transition de données pas encore ouverte (UnitsLayers l'ouvre dans son effet) :
  // les symboles gardent ce qu'ils affichent jusque-là.
  const held = useRef<{ id: number; at: number } | null>(null);
  const [ticking, setTicking] = useState(false);
  const tick = useTicker(ticking);
  const now = ticking ? tick : performance.now();

  const next = data && !isPlaceholderData ? dataSignature(data, exerciseId) : null;
  if (next && signatureChanged(signature.current, next)) {
    if (isDataTransition(signature.current, next)) held.current = { id, at: now };
    signature.current = next;
  }
  // La transition ouverte (nouveau numéro), ou pas ouverte du tout (sécurité) : le plan suit les données.
  if (held.current !== null && (held.current.id !== id || now - held.current.at > HOLD_MS)) held.current = null;
  // Sécurité : sans transition ouverte, aucun rendu n'est programmé (horloge locale à l'arrêt) ; un rendu forcé
  // à l'échéance libère les symboles retenus sur l'ancien plan.
  const [, forceRender] = useState(0);
  const heldAt = held.current?.at ?? null;
  useEffect(() => {
    if (heldAt === null) return;
    const timer = window.setTimeout(() => forceRender((n) => n + 1), HOLD_MS + 20);
    return () => window.clearTimeout(timer);
  }, [heldAt]);

  const inputs: unknown[] = [data, phase, visible.AMI, visible.ENI, supportsVisible, level, levelSymbols, roots];
  const previous = current.current;
  if (held.current === null || previous === null) {
    const clockChanged = previous !== null && previous.seen !== id;
    const inputsChanged = previous === null || previous.inputs.some((value, i) => value !== inputs[i]);
    if (clockChanged || inputsChanged) {
      // Nouveau plan depuis ce qui est affiché : sur l'horloge commune si une transition vient de s'ouvrir (temps
      // ou réponse du serveur), sinon local (calque masqué ou affiché, aperçu du brouillon).
      // Symboles des unités d'appui et de soutien cachées : cachés avec elles.
      const hiddenUnits = new Set(
        (data?.units ?? []).filter((u) => !unitShown(u, supportsVisible)).flatMap((u) => [u.key, u.code]),
      );
      // Carte zoomée : symboles de la profondeur affichée, à la place de ceux du dossier (un seul niveau à la fois).
      const source =
        data && level && levelSymbols
          ? levelMissionSymbols(level, levelSymbols, phase, data.units, roots, supportsVisible)
          : anchoredSymbols(
              (data?.mission_symbols[phase] ?? []).filter((s) => !hiddenUnits.has(s.unit)),
              data?.units ?? [],
              phase,
            );
      const target = keyMissionSymbols(source.filter((s) => visible[s.side]));
      const shown = displayed.current.map(({ drawn: _drawn, ...s }) => s as ShownSymbol);
      const entryOrigin = clockChanged && kind === "phase" ? phaseEntryOrigin(data, from, phase, shown) : undefined;
      current.current = {
        plan: pairMissionSymbols(shown, target, { entryOrigin }),
        clock: clockChanged ? id : null,
        seen: id,
        started: now,
        inputs,
      };
    }
  }
  const plan = current.current!;
  const eased =
    plan.clock !== null
      ? smoothstep(plan.clock === id ? progress : 1)
      : smoothstep(Math.min(1, (now - plan.started) / MAP_EXIT_MS));
  const frames = frameMissionSymbols(plan.plan, eased, reveal);
  displayed.current = frames;
  const localRunning = plan.clock === null && eased < 1;

  useEffect(() => {
    if (localRunning !== ticking) setTicking(localRunning);
  });

  // Orientation de la carte : lue à chaque rendu ; pendant une rotation, un rendu n'est demandé que si une image
  // montée change (seuil franchi).
  const [, setFlips] = useState("");
  const flips = useRef("");
  const hasData = Boolean(data);
  useEffect(() => {
    const map = mapRef?.current?.getMap();
    if (!map || !hasData) return;
    const follow = () => {
      const next = shownImages(displayed.current, map.getBearing());
      if (next !== flips.current) setFlips(next);
    };
    follow();
    map.on("rotate", follow);
    return () => {
      map.off("rotate", follow);
    };
  }, [mapRef, hasData]);
  const bearing = mapRef?.current?.getMap()?.getBearing() ?? 0;
  flips.current = shownImages(frames, bearing);

  // Données inchangées (rendu sans transition ni bascule) : même objet, aucune mise à jour de la source.
  const collection = useRef<{ json: string; data: FeatureCollection } | null>(null);
  if (!data) return null;
  // Symboles poussés : un calque chacun (`icon-translate` n'est pas piloté par les données).
  const pushed: { key: string; translate: unknown[] }[] = [];
  const features: Feature<Point>[] = frames.map((s) => {
    const clear = clearanceOf(s, bearing);
    if (clear) pushed.push({ key: s.key, translate: translateExpression(clear, bearing) });
    const shown = shownMissionImage(s.image, s.rotation, bearing, s.offset);
    return {
      type: "Feature",
      geometry: { type: "Point", coordinates: s.at },
      properties: {
        // Verbe sans image (niveau compagnie) : pastille neutre droite, qui porte le verbe.
        icon: s.image === NEUTRAL_IMAGE ? neutralIcon(s.mission, lang()) : `${MISSION_PREFIX}${shown.image}`,
        rotation: s.image === NEUTRAL_IMAGE ? 0 : s.rotation,
        scale: s.scale,
        shifted: s.offset !== null,
        offset: shown.offset ?? [0, 0],
        opacity: s.drawn,
        key: s.key,
        own: clear !== null,
      },
    };
  });
  const json = JSON.stringify(features);
  if (collection.current?.json !== json) {
    collection.current = { json, data: { type: "FeatureCollection", features } as FeatureCollection };
  }
  const symbols = collection.current.data;
  return (
    <Source id="gx-mission-symbols" type="geojson" data={symbols}>
      <Layer
        id="mission-symbols"
        type="symbol"
        filter={["all", ["!=", ["get", "shifted"], true], ["!=", ["get", "own"], true]]}
        layout={LAYOUT}
        paint={{ "icon-opacity": ["get", "opacity"] }}
      />
      <Layer
        id="mission-symbols-shifted"
        type="symbol"
        filter={["==", ["get", "shifted"], true]}
        layout={SHIFTED_LAYOUT}
        paint={{ "icon-opacity": ["get", "opacity"] }}
      />
      {pushed.map(({ key, translate }) => (
        <Layer
          key={key}
          id={`${OWN_LAYER_PREFIX}${key}`}
          beforeId="mission-symbols"
          type="symbol"
          filter={["==", ["get", "key"], key]}
          layout={OWN_LAYOUT}
          paint={{ "icon-opacity": ["get", "opacity"], "icon-translate": translate, "icon-translate-anchor": "viewport" }}
        />
      ))}
    </Source>
  );
}
